import { initWebVitalsTelemetry } from "./web-vitals";
import { initMobileNav } from "./nav";
import { initScrollReveal } from "./scroll-reveal";
import { trackEvent } from "./ga-events";

(function () {
  "use strict";

  initWebVitalsTelemetry();
  initMobileNav({ headerId: "header", toggleSelector: ".nav-toggle", linksId: "nav-links", backdropId: "nav-backdrop" });
  initScrollReveal();

  /* ---- Smooth scroll for same-page anchors ---- */
  document.querySelectorAll('a[href^="#"]').forEach(function (anchor) {
    anchor.addEventListener("click", function () {
      var id = this.getAttribute("href");
      if (id === "#") return;
      var target = document.querySelector(id);
      if (target) {
        target.setAttribute("tabindex", "-1");
        target.focus({ preventScroll: true });
      }
    });
  });

  /* ---- Get Started form → navigate to sign-in with prefilled email ---- */
  var getStartedForm = document.getElementById("fhqGetStartedForm");
  if (getStartedForm) {
    getStartedForm.addEventListener("submit", function (e) {
      e.preventDefault();
      trackEvent("sign_up_start", { source: "familyhq_landing" });
      var emailEl = getStartedForm.querySelector("input[type='email']");
      var email = emailEl ? emailEl.value.trim() : "";
      var url = "/SignIn";
      if (email) {
        url += "?email=" + encodeURIComponent(email);
      }
      window.location.href = url;
    });
  }

  /* ---- Sticky email CTA ---- */
  var stickyCta = document.querySelector("[data-fhq-sticky-cta]");
  if (stickyCta && stickyCta.dataset.initialized !== "true") {
    stickyCta.dataset.initialized = "true";

    var DISMISS_DAYS = 7;
    var REVEAL_DELAY_MS = 2000;
    var dismissKey = stickyCta.getAttribute("data-dismiss-key") || "fhq_sticky_email_cta_v1";
    var analyticsSource = stickyCta.getAttribute("data-analytics-source") || "familyhq_sticky_cta";
    // "newsletter" (default) = inline double opt-in capture; "signin" = funnel into the app sign-up flow.
    var ctaMode = stickyCta.getAttribute("data-fhq-sticky-cta-mode") || "newsletter";
    var submittedKey = "fhq_sticky_email_cta_submitted";
    var suppressRegions = [];
    var suppressRegionState = typeof WeakMap === "function" ? new WeakMap() : null;
    var suppressRegionVisible = false;
    var revealDelayElapsed = false;
    var dismissed = false;
    var submitted = false;

    function storageGet(key) {
      try { return window.localStorage.getItem(key); } catch (_) { return null; }
    }

    function storageSet(key, value) {
      try { window.localStorage.setItem(key, value); } catch (_) { }
    }

    // Publish the bar's current height (0 when hidden) so the concierge launcher pill can sit above it,
    // and flag while the bar is up so the concierge launcher hides instead of stacking on top of it
    // (two bottom-anchored CTAs collided with the content on small screens).
    function syncLauncherOffset() {
      var visible = stickyCta.classList.contains("is-visible");
      var height = visible ? stickyCta.offsetHeight : 0;
      document.documentElement.style.setProperty("--fhq-sticky-cta-height", height + "px");
      document.documentElement.classList.toggle("fhq-sticky-cta-shown", visible);
    }

    // Set the status line + a state class so success reads green and only real errors read red.
    function setStickyStatus(el, message, state) {
      if (!el) return;
      el.textContent = message;
      el.classList.toggle("is-success", state === "success");
      el.classList.toggle("is-error", state === "error");
    }

    function isFreshTimestamp(value) {
      if (!value) return false;
      var parsed = new Date(value).getTime();
      return Number.isFinite(parsed) && (Date.now() - parsed < DISMISS_DAYS * 864e5);
    }

    dismissed = isFreshTimestamp(storageGet(dismissKey));
    submitted = isFreshTimestamp(storageGet(submittedKey));

    function shouldShow() {
      return revealDelayElapsed && !dismissed && !submitted && !suppressRegionVisible;
    }

    function updateStickyCta() {
      if (shouldShow()) {
        stickyCta.hidden = false;
        stickyCta.setAttribute("aria-hidden", "false");
        window.setTimeout(function () {
          if (shouldShow()) { stickyCta.classList.add("is-visible"); syncLauncherOffset(); }
        }, 0);
      } else {
        stickyCta.classList.remove("is-visible");
        stickyCta.setAttribute("aria-hidden", "true");
        syncLauncherOffset();
        window.setTimeout(function () {
          if (!shouldShow()) stickyCta.hidden = true;
        }, 260);
      }
    }

    function addSuppressRegion(element) {
      if (!element || suppressRegions.indexOf(element) !== -1) return;
      suppressRegions.push(element);
      if (suppressRegionState) suppressRegionState.set(element, false);
    }

    function updateSuppressRegionVisibility(entries) {
      if (!suppressRegionState) return;
      entries.forEach(function (entry) {
        suppressRegionState.set(entry.target, entry.isIntersecting);
      });
      suppressRegionVisible = suppressRegions.some(function (element) {
        return suppressRegionState.get(element) === true;
      });
      updateStickyCta();
    }

    var dismissButton = stickyCta.querySelector("[data-fhq-sticky-cta-dismiss]");
    if (dismissButton) {
      dismissButton.addEventListener("click", function () {
        dismissed = true;
        storageSet(dismissKey, new Date().toISOString());
        updateStickyCta();
      });
    }

    document.addEventListener("keydown", function (e) {
      if (e.key === "Escape" && stickyCta.classList.contains("is-visible")) {
        dismissed = true;
        storageSet(dismissKey, new Date().toISOString());
        updateStickyCta();
      }
    });

    addSuppressRegion(document.getElementById("get-started"));
    addSuppressRegion(document.querySelector(".fhq-footer"));
    if (suppressRegions.length && suppressRegionState && "IntersectionObserver" in window) {
      var io = new IntersectionObserver(updateSuppressRegionVisibility, {
        rootMargin: "0px 0px 24px 0px",
        threshold: 0.02
      });
      suppressRegions.forEach(function (element) {
        io.observe(element);
      });
    }

    window.setTimeout(function () {
      revealDelayElapsed = true;
      updateStickyCta();
    }, REVEAL_DELAY_MS);

    window.addEventListener("resize", function () {
      if (stickyCta.classList.contains("is-visible")) syncLauncherOffset();
    });

    var stickyForm = stickyCta.querySelector("[data-fhq-sticky-cta-form]");
    if (stickyForm) {
      stickyForm.addEventListener("submit", function (e) {
        e.preventDefault();

        var emailInput = stickyForm.querySelector("input[name='email']");
        var status = stickyForm.querySelector("[data-fhq-sticky-cta-status]");
        var submitButton = stickyForm.querySelector("button[type='submit']");
        var email = emailInput ? emailInput.value.trim() : "";
        var emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

        if (!emailPattern.test(email)) {
          setStickyStatus(status, "Enter a valid email address.", "error");
          if (emailInput) emailInput.focus();
          return;
        }

        // Sign-up funnel mode: hand the email off to /SignIn instead of capturing a newsletter subscription.
        if (ctaMode === "signin") {
          setStickyStatus(status, "Opening sign in...", "pending");
          submitted = true;
          storageSet(submittedKey, new Date().toISOString());
          trackEvent("sign_up_start", { source: analyticsSource });
          var signInAction = stickyForm.getAttribute("action") || "/SignIn";
          window.location.href = signInAction +
            (signInAction.indexOf("?") === -1 ? "?" : "&") + "email=" + encodeURIComponent(email);
          return;
        }

        setStickyStatus(status, "Signing you up...", "pending");
        if (submitButton) submitButton.disabled = true;

        fetch(stickyForm.getAttribute("action") || "/newsletter/subscribe", {
          method: "POST",
          headers: { "X-Requested-With": "XMLHttpRequest" },
          body: new FormData(stickyForm),
          credentials: "same-origin"
        })
          .then(function (response) {
            return response.json().catch(function () { return {}; });
          })
          .then(function (data) {
            if (data && data.success) {
              submitted = true;
              storageSet(submittedKey, new Date().toISOString());
              trackEvent("newsletter_signup", { source: analyticsSource });
              setStickyStatus(status, data.message || "Check your inbox to confirm.", "success");
            } else {
              if (submitButton) submitButton.disabled = false;
              setStickyStatus(status, (data && data.error) || "Something went wrong. Please try again.", "error");
            }
          })
          .catch(function () {
            if (submitButton) submitButton.disabled = false;
            setStickyStatus(status, "Network error. Please try again.", "error");
          });
      });

      var stickyEmailInput = stickyForm.querySelector("input[name='email']");
      var stickyStatus = stickyForm.querySelector("[data-fhq-sticky-cta-status]");
      if (stickyEmailInput && stickyStatus) {
        stickyEmailInput.addEventListener("input", function () {
          setStickyStatus(stickyStatus, "", "pending");
        });
      }
    }
  }
})();
